/* Mobile: native touch scrolling. Desktop: retain selected-variant image and hover. */
(() => {
  if (window.monateoNativeCardsLoaded) return;
  window.monateoNativeCardsLoaded = true;
  const controllers = new Map();
  const mobile = matchMedia('(max-width: 767px)');
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initialize(card) {
    if (controllers.has(card)) return;
    const track = card.querySelector('.slick-slider-wrapper');
    if (!track) return;
    const slides = [...track.querySelectorAll('.slide-item')];
    if (!slides.length) return;
    const selectors = [...card.querySelectorAll('.product-variant-selector')];
    const indicator = card.querySelector('.product-card__progress-indicator');
    const abort = new AbortController();
    const listen = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: abort.signal });
    let selected = selectors.find(s => s.classList.contains('is-selected') || s.classList.contains('selected')) || selectors[0];
    let active = Math.max(0, slides.findIndex(s => s.dataset.mediaId === selected?.dataset.variantMediaId));
    let clones = [];
    let timer;
    let touching = false;
    let settling = false;
    let frame;
    let down;
    let suppressClickUntil = 0;
    let lastWidth = 0;

    function variantFor(index) {
      let result = null;
      for (let i = 0; i <= index; i++) {
        const match = selectors.find(s => s.dataset.variantMediaId === slides[i].dataset.mediaId);
        if (match) result = match;
      }
      return result || selected;
    }
    function update(index, selectFromImage = true) {
      active = Math.max(0, Math.min(index, slides.length - 1));
      if (selectFromImage) selected = variantFor(active);
      selectors.forEach(s => {
        s.classList.toggle('is-selected', s === selected);
        s.classList.toggle('selected', s === selected);
      });
      if (selected?.dataset.variantId) {
        const link = card.querySelector('.product-image-link');
        const base = selected.dataset.variantUrl || link?.dataset.productUrl || link?.getAttribute('href');
        if (base) {
          const url = new URL(base, location.origin);
          url.searchParams.set('variant', selected.dataset.variantId);
          card.querySelectorAll('.product-image-link, .card__heading a.full-unstyled-link').forEach(a => a.href = url.href);
        }
      }
      if (indicator) indicator.style.transform = `translateX(${active * 100 / slides.length}%) scaleX(${1 / slides.length})`;
      if (!mobile.matches) slides.forEach((slide, i) => {
        slide.classList.toggle('is-visible', i === active);
        slide.classList.remove('is-hover');
      });
    }
    function position(physicalIndex) {
      const item = track.children[physicalIndex];
      return item ? track.scrollLeft + item.getBoundingClientRect().left - track.getBoundingClientRect().left : 0;
    }
    function jump(index) {
      // Disable snapping for the invisible wrap from a clone to its original.
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: position(index), behavior: 'instant' });
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { track.style.scrollSnapType = ''; });
    }
    function nearest() {
      const left = track.getBoundingClientRect().left;
      let nearestIndex = 0, distance = Infinity;
      [...track.children].forEach((slide, i) => {
        const delta = Math.abs(slide.getBoundingClientRect().left - left);
        if (delta < distance) { distance = delta; nearestIndex = i; }
      });
      return nearestIndex;
    }
    function settled() {
      if (!mobile.matches || touching || !track.clientWidth) return;
      const physical = nearest();
      const offset = clones.length ? 1 : 0;
      const index = (physical - offset + slides.length) % slides.length;
      update(index);
      if (clones.length && (physical === 0 || physical === slides.length + 1)) jump(index + 1);
      settling = false;
    }
    function goTo(index) {
      update(index, false);
      if (!mobile.matches) return;
      track.scrollTo({ left: position(active + (clones.length ? 1 : 0)), behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    function layout() {
      clearTimeout(timer);
      clones.forEach(c => c.remove()); clones = [];
      track.classList.toggle('monateo-native-track', mobile.matches);
      slides.forEach(s => s.classList.remove('is-visible', 'is-hover'));
      if (mobile.matches && slides.length > 1) {
        clones = [slides.at(-1).cloneNode(true), slides[0].cloneNode(true)];
        clones.forEach(c => {
          c.dataset.cardClone = 'true'; c.setAttribute('aria-hidden', 'true'); c.inert = true;
          c.removeAttribute('id'); c.querySelectorAll('[id]').forEach(e => e.removeAttribute('id'));
        });
        track.prepend(clones[0]); track.append(clones[1]);
      }
      update(active, false);
      if (mobile.matches) jump(active + (clones.length ? 1 : 0));
      else { track.scrollLeft = 0; track.style.scrollSnapType = ''; }
      lastWidth = track.clientWidth;
    }
    listen(track, 'scroll', () => {
      if (!mobile.matches) return;
      settling = true;
      clearTimeout(timer); timer = setTimeout(settled, 120);
    }, { passive: true });
    listen(track, 'scrollend', settled);
    listen(track, 'pointerdown', e => { down = { x: e.clientX, y: e.clientY }; touching = true; clearTimeout(timer); }, { passive: true });
    listen(track, 'pointermove', e => {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 8) suppressClickUntil = Date.now() + 400;
    }, { passive: true });
    const end = () => { touching = false; down = null; clearTimeout(timer); timer = setTimeout(settled, 120); };
    listen(track, 'pointerup', end, { passive: true });
    listen(track, 'pointercancel', end, { passive: true });
    listen(track, 'click', e => {
      if (Date.now() < suppressClickUntil) { e.preventDefault(); e.stopPropagation(); }
    }, { capture: true });
    selectors.forEach(selector => listen(selector, 'click', e => {
      e.preventDefault(); selected = selector;
      const index = slides.findIndex(s => s.dataset.mediaId === selector.dataset.variantMediaId);
      if (index >= 0) goTo(index); else update(active, false);
    }));
    const container = card.querySelector('.product-card-carousel') || track;
    listen(container, 'mouseenter', () => {
      if (mobile.matches || slides.length < 2) return;
      const next = slides[(active + 1) % slides.length];
      slides[active].classList.remove('is-visible'); next.classList.add('is-hover');
    });
    listen(container, 'mouseleave', () => { if (!mobile.matches) update(active, false); });
    const observer = new ResizeObserver(() => {
      if (!mobile.matches || !track.clientWidth || track.clientWidth === lastWidth) return;
      lastWidth = track.clientWidth;
      if (!touching && !settling) jump(active + (clones.length ? 1 : 0));
    });
    observer.observe(track);
    controllers.set(card, { layout, destroy() { abort.abort(); observer.disconnect(); clearTimeout(timer); cancelAnimationFrame(frame); } });
    card.classList.add('js-custom-card-init');
    layout();
  }
  function scan(root) {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches?.('.product-card-wrapper')) initialize(root);
    root.querySelectorAll('.product-card-wrapper').forEach(initialize);
  }
  function start() {
    scan(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(scan));
      controllers.forEach((controller, card) => {
        if (!card.isConnected) { controller.destroy(); controllers.delete(card); }
      });
    }).observe(document.body, { childList: true, subtree: true });
    mobile.addEventListener('change', () => controllers.forEach(c => c.layout()));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
